import { type FC, type ReactNode, useEffect, useRef } from "react";
import { useLocation } from "react-router";

import { faBars } from "@fortawesome/pro-duotone-svg-icons/faBars";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useIsDesktop } from "@application/hooks/use_is_desktop";
import { usePushSubscriptionSync } from "@application/hooks/use_push_notifications";
import { useToggle } from "@application/hooks/use_toggle";

import { DashboardRail } from "./dashboard/dashboard_rail";
import { NavRailContext } from "@application/contexts/nav_rail_context";

// One level into a case the nav recedes to the thin icon rail; everywhere else
// it's the list-level status/builder rail.
const CASE_DETAIL = /^\/dashboard\/projects\/[^/]+/;

export const DashboardLayout: FC<{
  children: ReactNode;
}> = ({ children }) => {
  usePushSubscriptionSync();

  // `open` drives the wide rail's off-canvas drawer below md (it would otherwise
  // eat half a narrow screen) — at every level on mobile. `railHidden` is the
  // desktop-only ≡ slide for the thin case-detail rail.
  const [open, onToggle] = useToggle();
  const [railHidden, onToggleRail] = useToggle();
  const isDesktop = useIsDesktop();
  const location = useLocation();
  const { pathname } = location;
  const caseDetail = CASE_DETAIL.test(pathname);

  // The content area is its own scroll container (the layout chrome stays put),
  // so React keeps its scrollTop across route changes — a fresh page would open
  // mid-scroll. Reset it on every navigation.
  const contentRef = useRef<HTMLDivElement>(null);

  // Remember the last `?status=` the cases list was viewed at so the detail
  // rail's Home link can return to that filtered view (see DetailRail).
  const lastListSearch = useRef("");
  if (pathname === "/dashboard/projects")
    lastListSearch.current = location.search;

  // Close the mobile drawer + reset scroll after navigating (tapping a link).
  useEffect(() => {
    onToggle(false);
    contentRef.current?.scrollTo({ top: 0 });
  }, [pathname]);

  return (
    <NavRailContext.Provider
      value={{
        caseDetail,
        railHidden,
        // The banner ≡ slides the thin rail on desktop; below md it opens the
        // off-canvas drawer instead (there is no thin rail to slide).
        toggleRail: () => (isDesktop ? onToggleRail() : onToggle(true)),
        listSearch: lastListSearch.current,
        isDesktop,
      }}
    >
      <div className="ground flex h-screen">
        {open && (
          <div
            className="fixed inset-0 z-30 bg-slate-900/40 md:hidden"
            onClick={() => onToggle(false)}
          />
        )}

        {/* One persistent rail that folds 192px ↔ 56px between the list and
            case-detail levels on desktop, and drops to an off-canvas drawer
            below md (see DashboardRail). */}
        <DashboardRail open={open} />

        <div className="flex h-full min-w-0 shrink grow flex-col">
          {!caseDetail && (
            <div className="flex shrink-0 items-center border-b border-slate-200 bg-white px-4 py-3 md:hidden">
              <button
                type="button"
                onClick={() => onToggle(true)}
                aria-label="Open menu"
                className="text-slate-600"
              >
                <FontAwesomeIcon icon={faBars} />
              </button>
            </div>
          )}
          <div
            ref={contentRef}
            data-scroller=""
            className="min-h-0 flex-1 overflow-y-auto"
          >
            {children}
          </div>
        </div>
      </div>
    </NavRailContext.Provider>
  );
};
